/**
 * pasteConvert.ts — client wiring for the C-WP5 paste-to-syntax surface.
 *
 * Two seams:
 *   1. `createPasteConvert(client)` — the typed call into the server procedure
 *      `assistant.pasteConvert({ title, pastedText }) → { recipeUuid }`.
 *   2. `probePasteClaims()` — a read-only probe for the inline at-cap / opted-out
 *      states (assistant ops meter + opt-in). A read failure degrades to `null`;
 *      the pane then does NOT block the Convert affordance (the server guard +
 *      cap are the authority — an inline display probe must never gate the core
 *      creation action).
 *
 * W4-INTEGRATION: the server route (`assistant.pasteConvert`) is built by the
 * sibling w4-paste-route in parallel and is NOT yet in this tree. Until the
 * procedure lands on `AppRouter`, `createPasteConvert` reaches it through a
 * narrow typed cast so the swap to the generated client is mechanical (delete
 * the cast, keep the call). The request/response shapes are the frozen contract
 * both halves target (`@proof/client-core/assistant`).
 *
 * Build plan ref: .plan/implementation/2026-07-10-track-c-ai-mcp-build-plan.md
 *   C-WP5 — Paste-to-syntax.
 */

import { createApiClient, type ApiClient } from "@proof/client-core/api";
import type { PasteConvertRequest, PasteConvertResponse } from "@proof/client-core/assistant";
import type { PlanAiOps } from "../settings/planUsage.js";

/** A callable that performs one paste-convert. Returns the new recipe UUID. */
export type PasteConvertFn = (input: PasteConvertRequest) => Promise<PasteConvertResponse>;

/**
 * The assistant sub-router shape this feature needs, once `pasteConvert` lands.
 * Declared locally so the cast in {@link createPasteConvert} is typed (not a
 * bare `any`) — the swap to the generated client is: drop this interface + the
 * cast, call `client.assistant.pasteConvert(input)` directly.
 */
interface AssistantWithPasteConvert {
  pasteConvert: (input: PasteConvertRequest) => Promise<PasteConvertResponse>;
}

/**
 * Build the paste-convert caller from an oRPC client.
 *
 * @param client - defaults to a same-origin client (matching every other in-app
 *   oRPC caller); pass an injected client in tests.
 */
export function createPasteConvert(
  client: ApiClient = createApiClient({ baseUrl: "" }),
): PasteConvertFn {
  return (input) => {
    // W4-INTEGRATION: replace with generated client. Once AppRouter carries
    // `assistant.pasteConvert`, this cast is unnecessary — call it directly.
    const assistant = client.assistant as unknown as AssistantWithPasteConvert;
    return assistant.pasteConvert(input);
  };
}

/** The claims the paste pane needs for its inline at-cap / opted-out states. */
export interface PasteClaims {
  /** Assistant-operations meter for the current window. */
  aiOps: PlanAiOps;
  /** Whether the plan resets weekly (paid) vs monthly (free) — drives reset copy. */
  isPaid: boolean;
  /** Whether the user has opted into the assistant (Settings → Assistant). */
  optedIn: boolean;
  /**
   * The ENVIRONMENT kill switch (`ASSISTANT_ENABLED`), mirrored onto the claims
   * payload. `false` on prod until the DPIA delta is signed. Read separately
   * from `optedIn` because `assistant.optIn.{get,set}` is deliberately not
   * behind the server guard, so the opt-in alone cannot tell a client whether a
   * dispatch would be accepted.
   */
  enabled: boolean;
}

/** A read-only probe for the paste pane's inline states. `null` ⇒ unavailable. */
export type PasteClaimsProbe = () => Promise<PasteClaims | null>;

/**
 * Read the assistant-ops meter + opt-in status for the current session. Combines
 * `entitlements.claims` (tier + ai-ops) and `assistant.optIn.get` in one round.
 *
 * Rule 8: a transport / parse failure resolves to `null` (logged, never
 * swallowed) — the pane treats "unavailable" as "do not block the Convert
 * affordance"; the server guard/cap remain authoritative.
 */
export async function probePasteClaims(
  client: ApiClient = createApiClient({ baseUrl: "" }),
): Promise<PasteClaims | null> {
  try {
    const [claims, optIn] = await Promise.all([
      client.entitlements.claims({}),
      client.assistant.optIn.get({}),
    ]);
    return {
      aiOps: {
        used: claims.aiOps.used,
        cap: claims.aiOps.cap,
        resetsAt: claims.aiOps.resetsAt,
      },
      isPaid: claims.tier === "paid",
      optedIn: optIn.optIn,
      enabled: claims.assistantEnabled === true,
    };
  } catch (err) {
    console.error("[proof.paste-convert] claims probe failed", err);
    return null;
  }
}
